import {
  CANVAS_AI_APP_NAME,
  CANVAS_AI_MAX_OUTPUT_TOKENS,
  CANVAS_AI_MAX_STEPS,
  CANVAS_AI_TEMPERATURE,
} from "./canvasAi.constants";
import { buildCanvasAiSystemPrompt } from "./canvasAiContext";
import { applyCanvasAiPatch } from "./canvasAiPatch";
import { canvasAiPatchSchema } from "./canvasAi.schema";

const GEMMA_4_MODEL_PREFIX = "google/gemma-4-";

const getAppUrl = () =>
  typeof window === "undefined" ? "https://netpad.click" : window.location.origin;

const mergeStats = (current, next) => ({
  added: current.added + next.added,
  deleted: current.deleted + next.deleted,
  updated: current.updated + next.updated,
});

const createDraftController = (checkpoint) => {
  let elements = checkpoint.elements;
  const changedIds = new Set();
  let hasPatch = false;
  let lastError = null;
  let stats = { added: 0, deleted: 0, updated: 0 };
  let summary = "";

  return {
    apply: (patch) => {
      try {
        const result = applyCanvasAiPatch({ elements, patch });
        elements = result.elements;
        hasPatch = true;
        result.changedIds.forEach((elementId) => changedIds.add(elementId));
        lastError = null;
        stats = mergeStats(stats, result.stats);
        summary = result.summary;
        return { ok: true, stats: result.stats, summary: result.summary };
      } catch (error) {
        lastError = error;
        throw error;
      }
    },
    getResult: () => ({ changedIds, elements, hasPatch, lastError, stats, summary }),
  };
};

const createCanvasPatchTool = ({ draft, toolFactory }) =>
  toolFactory({
    description:
      "Atomically add, update, or delete semantic canvas diagram elements. " +
      "Use temporary refs for new elements and element IDs for existing ones.",
    execute: async (patch) => draft.apply(patch),
    inputSchema: canvasAiPatchSchema,
  });

const createProvider = ({ apiKey, createOpenRouter }) =>
  createOpenRouter({
    apiKey,
    appName: CANVAS_AI_APP_NAME,
    appUrl: getAppUrl(),
    compatibility: "strict",
  });

const loadAiRuntime = async () => {
  const [providerModule, aiModule] = await Promise.all([
    import("@openrouter/ai-sdk-provider"),
    import("ai"),
  ]);
  return {
    createOpenRouter: providerModule.createOpenRouter,
    generateText: aiModule.generateText,
    stepCountIs: aiModule.stepCountIs,
    streamText: aiModule.streamText,
    toolFactory: aiModule.tool,
  };
};

const createAbortError = () => {
  const error = new Error("Generation aborted");
  error.name = "AbortError";
  return error;
};

export const consumeCanvasAiStream = async ({ onText, stream }) => {
  let streamError = null;
  let text = "";
  for await (const part of stream) {
    if (part.type === "text-delta") {
      text += part.text;
      onText?.(text);
    }
    if (part.type === "error" && !streamError) streamError = part.error;
    if (part.type === "abort" && !streamError) streamError = createAbortError();
  }

  return { streamError, text: text.trim() };
};

export const shouldBufferCanvasAiModel = (modelId) =>
  String(modelId).startsWith(GEMMA_4_MODEL_PREFIX);

const createTurnOptions = ({
  abortSignal,
  checkpoint,
  draft,
  messages,
  model,
  runtime,
}) => ({
  abortSignal,
  instructions: buildCanvasAiSystemPrompt(checkpoint),
  maxOutputTokens: CANVAS_AI_MAX_OUTPUT_TOKENS,
  messages,
  model,
  stopWhen: runtime.stepCountIs(CANVAS_AI_MAX_STEPS),
  temperature: CANVAS_AI_TEMPERATURE,
  toolChoice: "auto",
  tools: {
    apply_canvas_patch: createCanvasPatchTool({
      draft,
      toolFactory: runtime.toolFactory,
    }),
  },
});

const runBufferedTurn = async ({ onText, options, runtime }) => {
  const result = await runtime.generateText(options);
  const text = result.text.trim();
  if (text) onText?.(text);
  return text;
};

const runStreamedTurn = async ({ onText, options, runtime }) => {
  let callbackError = null;
  const result = runtime.streamText({
    ...options,
    onError: ({ error }) => {
      callbackError = callbackError ?? error;
    },
  });
  const consumed = await consumeCanvasAiStream({ onText, stream: result.stream });
  const streamError = consumed.streamError ?? callbackError;
  if (streamError) throw streamError;
  return consumed.text;
};

/**
 * Runs one model turn against an isolated scene draft.
 * @param {{ abortSignal?: AbortSignal, apiKey: string, checkpoint: object, messages: Array<object>, modelId: string, onText?: Function }} params
 * @returns {Promise<object>}
 */
export const runCanvasAiTurn = async ({
  abortSignal,
  apiKey,
  checkpoint,
  messages,
  modelId,
  onText,
}) => {
  const runtime = await loadAiRuntime();
  const draft = createDraftController(checkpoint);
  const openrouter = createProvider({
    apiKey,
    createOpenRouter: runtime.createOpenRouter,
  });
  const options = createTurnOptions({
    abortSignal,
    checkpoint,
    draft,
    messages,
    model: openrouter.chat(modelId, {
      provider: { require_parameters: true },
    }),
    runtime,
  });
  const text = shouldBufferCanvasAiModel(modelId)
    ? await runBufferedTurn({ onText, options, runtime })
    : await runStreamedTurn({ onText, options, runtime });
  const draftResult = draft.getResult();
  if (!draftResult.hasPatch && draftResult.lastError && !text) {
    throw draftResult.lastError;
  }

  return { ...draftResult, text };
};
